import {
  Button,
  Checkbox,
  ConfirmationDialog,
  cn,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  EraserIcon,
  FileTextIcon,
  type Icon,
  ProhibitIcon,
  QuestionIcon,
  UsersThreeIcon,
  XIcon,
} from "@phosphor-icons/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useStickyHeaderOffsets } from "@/lib/utils/sticky-table-header";
import {
  confirmDiscardDraft,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";

export type MatrixContract = {
  id: number;
  label: string;
  shortName: string | null;
  type: "travaux_n_1" | "travaux_n_2";
  groupId: number | null;
  /** Rang 2 only: the rang 1 contract it is sub-contracted from. */
  parentContractId: number | null;
};

export type MatrixTeam = {
  id: number;
  name: string;
  rang: 1 | 2;
  contractId: number;
};

export type MatrixGroup = {
  id: number;
  mandataireContractId: number | null;
};

type CellState = {
  contractIds: number[];
  teamRang1Id: number | null;
  teamRang2Id: number | null;
  bossTeamIds: number[];
  /** Intervention connue mais périmètre pas encore défini (mode "?"). */
  keepEmpty: boolean;
  /**
   * Case « non concernée » : aucune intervention n'y aura lieu, elle
   * reste vierge quelle que soit la pré-sélection appliquée.
   */
  excluded: boolean;
};

type Lot = {
  id: number;
  nLot: string | null;
  name: string;
  performances: { id: number; name: string }[];
};

type Building = {
  id: number;
  name: string;
  shortName: string | null;
  zones: { id: number; name: string; shortName: string | null }[];
};

export type InterventionMatrixProps = {
  yardId: number;
  contracts: MatrixContract[];
  /** Teams eligible for new assignments (interrupted contracts' teams excluded). */
  teams: MatrixTeam[];
  /**
   * Every team, including those under an interrupted contract. Used only to
   * resolve names for already-assigned cells so they keep displaying even
   * after their contract is interrupted — `teams` stays restricted for
   * pickers/options.
   */
  allTeams: MatrixTeam[];
  /** Every contract, including interrupted ones — mirrors `allTeams`, used
   * only to resolve abbreviations for already-assigned cells. */
  allContracts: MatrixContract[];
  groups: MatrixGroup[];
  /**
   * The yard is commissioned: the contract in charge of an intervention can
   * no longer be changed or removed here, only added where none is active
   * (e.g. after an interruption). Before commissioning a change is allowed
   * but confirmed on save.
   */
  contractsLocked: boolean;
  /**
   * Rendered first in the matrix's own toolbar row, ahead of the
   * "Pré-sélection :" controls — the page's title + "Gérer prestas" button,
   * so both share one line instead of two stacked rows and leave more height
   * for the grid itself.
   */
  headerContent?: ReactNode;
  /**
   * Where to render the draft's Enregistrer/Annuler (e.g. the screen header),
   * so they don't push the toolbar onto a second line. Inline in the toolbar
   * when absent.
   */
  draftActionsTarget?: HTMLElement | null;
};

type GridPosition = { row: number; col: number };

/**
 * Pré-sélection: chosen before ticking cells, it decides what each
 * ticked cell receives, with this cascade:
 * - intervention: the intervention only (cell marked "?")
 * - contractRang1: + the rang 1 contract
 * - teamRang1: + the team's rang 1 contract + the team
 * - contractRang2: + the rang 1 contract it is sub-contracted from + the rang 2 contract
 * - teamRang2: + that rang 1 contract + the team's rang 2 contract + the team
 * - clear: empties the cell (and lifts a « non concernée » mark)
 * - exclude: marks the cell « non concernée »; other pré-sélections then
 *   leave it blank, row and column fills included
 * - pilotTeam (picked under « Équipe rang 1 »): makes a team of a
 *   groupement's mandataire the cell's single pilot, replacing any other,
 *   only on cells whose rang 1 contract is a non-mandataire member of that
 *   groupement
 *
 * A pré-sélection never overrides the hierarchy already in a cell: a rang 1
 * team only goes where its rang 1 contract (or none) is,
 * a rang 2 contract only under its own rang 1 contract, a rang 2 team only
 * under its own rang 2 and rang 1 contracts. Conflicting cells are skipped.
 */
type Preselection =
  | { kind: "intervention" }
  | { kind: "clear" }
  | { kind: "exclude" }
  | { kind: "contractRang1"; contractId: number }
  | { kind: "teamRang1"; teamId: number }
  | { kind: "contractRang2"; contractId: number }
  | { kind: "teamRang2"; teamId: number }
  | { kind: "pilotTeam"; teamId: number };

type PickerKind = "contractRang1" | "teamRang1" | "contractRang2" | "teamRang2";

const pickerTitles: Record<PickerKind, string> = {
  contractRang1: "Pré-sélectionner un contrat rang 1",
  teamRang1: "Pré-sélectionner une équipe rang 1",
  contractRang2: "Pré-sélectionner un contrat rang 2",
  teamRang2: "Pré-sélectionner une équipe rang 2",
};

const pickerEmptyMessages: Record<PickerKind, string> = {
  contractRang1: "Aucun contrat travaux rang 1 actif",
  teamRang1: "Aucune équipe rang 1 disponible",
  contractRang2: "Aucun contrat travaux rang 2 actif",
  teamRang2: "Aucune équipe rang 2 disponible",
};

const singlePilotHint =
  "Une seule équipe pilote par intervention : en choisir une autre remplace la précédente.";

/** Links a pré-sélection adds to a cell, cascade included. */
type CascadeLinks = {
  contractIds: number[];
  teamRang1Id: number | null;
  teamRang2Id: number | null;
};

const cellKey = (performanceId: number, zoneId: number) =>
  `${performanceId}:${zoneId}`;

const emptyCell: CellState = {
  contractIds: [],
  teamRang1Id: null,
  teamRang2Id: null,
  bossTeamIds: [],
  keepEmpty: false,
  excluded: false,
};

const isCellEmpty = (cell: CellState) =>
  cell.contractIds.length === 0 &&
  isNil(cell.teamRang1Id) &&
  isNil(cell.teamRang2Id) &&
  cell.bossTeamIds.length === 0;

/**
 * Sticky cells are bounded by the table, not their <tbody>: without this the
 * next lot's band would slide over the stuck one. Moves each stuck band up,
 * under the column headers, as the next band arrives. A scroll that ends with
 * the bands touching hides the stuck one instead of leaving it cut in half.
 */
export function pushLotBands(scroller: HTMLElement) {
  const headerHeight =
    scroller.querySelector("thead")?.getBoundingClientRect().height ?? 0;
  const stuckTop =
    scroller.getBoundingClientRect().top + scroller.clientTop + headerHeight;
  const atEnd =
    scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
  const lots = [...scroller.querySelectorAll("tbody")];
  lots.forEach((lot, index) => {
    const bandHeight = lot.rows.item(0)?.getBoundingClientRect().height ?? 0;
    const nextTop = lots[index + 1]?.getBoundingClientRect().top ?? Infinity;
    const overlap = Math.max(0, stuckTop + bandHeight - nextTop);
    const push = overlap > 0 && atEnd ? bandHeight : overlap;
    lot.style.setProperty("--lot-band-push", `${Math.min(push, bandHeight)}px`);
  });
}

export function InterventionMatrix({
  yardId,
  contracts,
  teams,
  allTeams,
  allContracts,
  groups,
  contractsLocked,
  headerContent,
  draftActionsTarget,
}: InterventionMatrixProps) {
  const [confirmingSave, setConfirmingSave] = useState(false);
  const [lots, setLots] = useState<Lot[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [serverCells, setServerCells] = useState<Map<string, CellState>>(
    new Map(),
  );
  // Cells left uncovered by an interrupted contract, awaiting reprise.
  const [needsReprise, setNeedsReprise] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<Map<string, CellState>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { headerRef, style: stickyOffsets } = useStickyHeaderOffsets();
  const [editing, setEditing] = useState<{
    performanceId: number;
    zoneId: number;
  } | null>(null);

  // Pré-sélection toolbar + cell ticking (click, shift+click, drag). The
  // pré-sélection survives a save so the user can keep ticking cells.
  const [preselection, setPreselection] = useState<Preselection | null>(null);
  const [pickerKind, setPickerKind] = useState<PickerKind | null>(null);
  const [selectionAnchor, setSelectionAnchor] = useState<GridPosition | null>(
    null,
  );
  const [selectionEnd, setSelectionEnd] = useState<GridPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  // Only the first load blanks the grid; a refetch after saving swaps the
  // data in place so the saved assignments show up right away.
  const fetchMatrix = useCallback(async () => {
    const { data, error } = await apiClient.GET(
      "/api/admin/yards/intervention-matrix",
      { params: { query: { yardId } } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Échec du chargement de la matrice",
      });
      setLoading(false);
      return;
    }
    setLots(data.data.lots);
    setBuildings(data.data.buildings);

    const cells = new Map<string, CellState>();
    const reprise = new Set<string>();
    for (const intervention of data.data.interventions) {
      const contractIds = intervention.contractIds;
      const teamRang1Id = intervention.teamRang1?.teamId ?? null;
      const teamRang2Id = intervention.teamRang2?.teamId ?? null;
      const bossTeamIds = intervention.bossTeams.map((b) => b.teamId);
      const key = cellKey(intervention.performanceId, intervention.zoneId);
      // An intervention row that persists with no links at all is a
      // placeholder created via the "?" mode: keep it that way by default.
      const keepEmpty =
        contractIds.length === 0 &&
        isNil(teamRang1Id) &&
        isNil(teamRang2Id) &&
        bossTeamIds.length === 0;
      cells.set(key, {
        contractIds,
        teamRang1Id,
        teamRang2Id,
        bossTeamIds,
        keepEmpty,
        excluded: false,
      });
      if (intervention.needsReprise) reprise.add(key);
    }
    for (const exclusion of data.data.exclusions) {
      cells.set(cellKey(exclusion.performanceId, exclusion.zoneId), {
        ...emptyCell,
        excluded: true,
      });
    }
    setServerCells(cells);
    setNeedsReprise(reprise);
    setDraft(new Map());
    setLoading(false);
  }, [yardId]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  useUnsavedChangesGuard(draft.size > 0);

  const getCell = useCallback(
    (performanceId: number, zoneId: number): CellState => {
      const key = cellKey(performanceId, zoneId);
      return draft.get(key) ?? serverCells.get(key) ?? emptyCell;
    },
    [draft, serverCells],
  );

  const isDirtyCell = useCallback(
    (performanceId: number, zoneId: number) =>
      draft.has(cellKey(performanceId, zoneId)),
    [draft],
  );

  const flatPerformances = useMemo(
    () => lots.flatMap((lot) => lot.performances),
    [lots],
  );
  const flatZones = useMemo(
    () => buildings.flatMap((building) => building.zones),
    [buildings],
  );

  const totalZones = flatZones.length;

  const teamName = useCallback(
    (teamId: number | null) =>
      isNotNil(teamId)
        ? (allTeams.find((t) => t.id === teamId)?.name ?? "?")
        : null,
    [allTeams],
  );

  // Abbreviation of the company performing the contract a team belongs to —
  // shown alongside the team name in matrix cells.
  const teamContractAbbrev = useCallback(
    (teamId: number | null) => {
      if (isNil(teamId)) return null;
      const team = allTeams.find((t) => t.id === teamId);
      if (isNil(team)) return null;
      const contract = allContracts.find((c) => c.id === team.contractId);
      return contract?.shortName ?? contract?.label ?? null;
    },
    [allTeams, allContracts],
  );

  // Abbreviations of a cell's contracts of one rang, the team's own contract
  // first: a cell shows its contracts even without a team.
  const cellContractAbbrevs = useCallback(
    (
      contractIds: number[],
      type: MatrixContract["type"],
      teamId: number | null,
    ): string[] => {
      const teamContractId = isNotNil(teamId)
        ? allTeams.find((t) => t.id === teamId)?.contractId
        : undefined;
      return allContracts
        .filter((c) => c.type === type && contractIds.includes(c.id))
        .sort(
          (a, b) =>
            Number(b.id === teamContractId) - Number(a.id === teamContractId),
        )
        .map((c) => c.shortName ?? c.label);
    },
    [allTeams, allContracts],
  );

  const rang1Teams = useMemo(() => teams.filter((t) => t.rang === 1), [teams]);
  const rang2Teams = useMemo(() => teams.filter((t) => t.rang === 2), [teams]);
  const rang1Contracts = useMemo(
    () => contracts.filter((c) => c.type === "travaux_n_1"),
    [contracts],
  );
  const rang2Contracts = useMemo(
    () => contracts.filter((c) => c.type === "travaux_n_2"),
    [contracts],
  );

  const contractLabel = useCallback(
    (contractId: number) =>
      contracts.find((c) => c.id === contractId)?.label ?? "?",
    [contracts],
  );

  // Rang 1 contract a rang 2 contract is sub-contracted from, when still
  // active (`contracts` only holds active ones): an interrupted parent must
  // first be replaced by the rang 1 contract taking over the mission.
  const parentRang1ContractId = useCallback(
    (rang2ContractId: number): number | null => {
      const parentId = contracts.find(
        (c) => c.id === rang2ContractId,
      )?.parentContractId;
      return rang1Contracts.find((c) => c.id === parentId)?.id ?? null;
    },
    [contracts, rang1Contracts],
  );

  // Contracts a team assignment implies: its own contract and, for a rang 2
  // team, the rang 1 contract above it.
  const teamContractIds = useCallback(
    (team: MatrixTeam): number[] => {
      if (team.rang === 1) return [team.contractId];
      const parentId = parentRang1ContractId(team.contractId);
      return isNotNil(parentId)
        ? [parentId, team.contractId]
        : [team.contractId];
    },
    [parentRang1ContractId],
  );

  // Contract whose teams may pilot a cell: the mandataire of the groupement
  // of the cell's rang 1 contract, when that contract is another member.
  const pilotContractIdFor = (contractIds: number[]): number | null => {
    const rang1 = allContracts.find(
      (c) => c.type === "travaux_n_1" && contractIds.includes(c.id),
    );
    const mandataireId = groups.find(
      (g) => g.id === rang1?.groupId,
    )?.mandataireContractId;
    return isNotNil(mandataireId) && mandataireId !== rang1?.id
      ? mandataireId
      : null;
  };

  const isValidPilot = (contractIds: number[], teamId: number) => {
    const pilotContractId = pilotContractIdFor(contractIds);
    return (
      isNotNil(pilotContractId) &&
      allTeams.find((t) => t.id === teamId)?.contractId === pilotContractId
    );
  };

  const cascadeLinks = (selection: Preselection): CascadeLinks | null => {
    switch (selection.kind) {
      case "intervention":
      case "clear":
      case "exclude":
      case "pilotTeam":
        return null;
      case "contractRang1":
        return {
          contractIds: [selection.contractId],
          teamRang1Id: null,
          teamRang2Id: null,
        };
      case "contractRang2": {
        const parentId = parentRang1ContractId(selection.contractId);
        return {
          contractIds: isNotNil(parentId)
            ? [parentId, selection.contractId]
            : [selection.contractId],
          teamRang1Id: null,
          teamRang2Id: null,
        };
      }
      case "teamRang1":
      case "teamRang2": {
        const team = teams.find((t) => t.id === selection.teamId);
        if (isNil(team)) return null;
        return {
          contractIds: teamContractIds(team),
          teamRang1Id: team.rang === 1 ? team.id : null,
          teamRang2Id: team.rang === 2 ? team.id : null,
        };
      }
    }
  };

  /** Whether `next` drops a contract the cell holds in base. */
  const dropsServerContract = (key: string, next: CellState) =>
    (serverCells.get(key)?.contractIds ?? []).some(
      (id) => !next.contractIds.includes(id),
    );

  /**
   * Stages a cell's new state. Returns false, leaving the cell untouched,
   * when it would change the contract in charge on a commissioned yard.
   */
  const applyCell = (
    performanceId: number,
    zoneId: number,
    requested: CellState,
  ): boolean => {
    const key = cellKey(performanceId, zoneId);
    if (contractsLocked && dropsServerContract(key, requested)) return false;
    const server = serverCells.get(key) ?? emptyCell;
    // Pilots no longer allowed by the cell's rang 1 contract go with it.
    const piloted = {
      ...requested,
      bossTeamIds: requested.bossTeamIds.filter((teamId) =>
        isValidPilot(requested.contractIds, teamId),
      ),
    };
    // Any intervention (links or "?") lifts the « non concernée » mark.
    const next =
      !isCellEmpty(piloted) || piloted.keepEmpty
        ? { ...piloted, excluded: false }
        : piloted;
    setDraft((prev) => {
      const updated = new Map(prev);
      // If the new state matches the server state, the cell is no longer dirty.
      const sameAsServer =
        server.contractIds.slice().sort().join(",") ===
          next.contractIds.slice().sort().join(",") &&
        server.teamRang1Id === next.teamRang1Id &&
        server.teamRang2Id === next.teamRang2Id &&
        server.bossTeamIds.slice().sort().join(",") ===
          next.bossTeamIds.slice().sort().join(",") &&
        server.keepEmpty === next.keepEmpty &&
        server.excluded === next.excluded;
      if (sameAsServer) {
        updated.delete(key);
      } else {
        updated.set(key, next);
      }
      return updated;
    });
    return true;
  };

  const warnLockedContracts = (count: number) => {
    if (count === 0) return;
    toaster.error({
      title: "Chantier en service",
      description:
        count === 1
          ? "Le contrat d'une intervention ne peut plus être modifié : interrompez d'abord le contrat en place pour qu'un autre le reprenne."
          : `Le contrat de ${count} interventions ne peut plus être modifié : interrompez d'abord le contrat en place pour qu'un autre le reprenne.`,
    });
  };

  const applyCellOrWarn = (
    performanceId: number,
    zoneId: number,
    next: CellState,
  ) => {
    if (!applyCell(performanceId, zoneId, next)) warnLockedContracts(1);
  };

  // Cells whose contract in charge the draft changes or removes.
  const reassignedCount = [...draft.entries()].filter(([key, state]) =>
    dropsServerContract(key, state),
  ).length;

  const save = async () => {
    setConfirmingSave(false);
    if (draft.size === 0) return;
    setSaving(true);
    const cells = [...draft.entries()].map(([key, state]) => {
      const [performanceId, zoneId] = key.split(":").map(Number);
      return {
        performanceId,
        zoneId,
        contractIds: state.contractIds,
        teamRang1Id: state.teamRang1Id,
        teamRang2Id: state.teamRang2Id,
        bossTeamIds: state.bossTeamIds,
        keepEmpty: state.keepEmpty,
        excluded: state.excluded,
      };
    });
    let error: { error?: string } | undefined;
    try {
      ({ error } = await apiClient.POST(
        "/api/admin/yards/intervention-matrix/save",
        { body: { yardId, cells } },
      ));
    } finally {
      setSaving(false);
    }
    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de l'enregistrement",
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Matrice enregistrée",
    });
    await fetchMatrix();
  };

  // --- Pré-sélection & cell ticking -----------------------------------------

  const hasServerIntervention = (performanceId: number, zoneId: number) =>
    serverCells.has(cellKey(performanceId, zoneId));

  /** Whether ticking this cell again should undo the pré-sélection. */
  const isPreselectionApplied = (
    cell: CellState,
    selection: Preselection,
  ): boolean => {
    switch (selection.kind) {
      case "intervention":
        return isCellEmpty(cell) && cell.keepEmpty;
      case "clear":
        return false;
      case "exclude":
        return cell.excluded;
      case "contractRang1":
      case "contractRang2":
        return cell.contractIds.includes(selection.contractId);
      case "teamRang1":
        return cell.teamRang1Id === selection.teamId;
      case "teamRang2":
        return cell.teamRang2Id === selection.teamId;
      case "pilotTeam":
        return cell.bossTeamIds.includes(selection.teamId);
    }
  };

  // Contracts of one rang currently in a cell.
  const cellContractsOfType = (
    cell: CellState,
    type: MatrixContract["type"],
  ): number[] =>
    cell.contractIds.filter(
      (id) => allContracts.find((c) => c.id === id)?.type === type,
    );

  /**
   * Whether a pré-sélection contradicts the contracts already in the cell
   * (hierarchy rule). An empty rang never conflicts.
   */
  const conflictsWithCell = (
    cell: CellState,
    selection: Preselection,
  ): boolean => {
    const rang1InCell = cellContractsOfType(cell, "travaux_n_1");
    const rang2InCell = cellContractsOfType(cell, "travaux_n_2");
    const rang1Conflicts = (rang1Id: number | null) =>
      rang1InCell.length > 0 &&
      (isNil(rang1Id) || !rang1InCell.includes(rang1Id));
    switch (selection.kind) {
      case "teamRang1": {
        const team = teams.find((t) => t.id === selection.teamId);
        return isNotNil(team) && rang1Conflicts(team.contractId);
      }
      case "contractRang2":
        return rang1Conflicts(parentRang1ContractId(selection.contractId));
      case "teamRang2": {
        const team = teams.find((t) => t.id === selection.teamId);
        if (isNil(team)) return false;
        return (
          (rang2InCell.length > 0 && !rang2InCell.includes(team.contractId)) ||
          rang1Conflicts(parentRang1ContractId(team.contractId))
        );
      }
      case "pilotTeam":
        return !isValidPilot(cell.contractIds, selection.teamId);
      default:
        return false;
    }
  };

  /** Cells a pré-sélection must leave untouched: « non concernée » or in conflict. */
  const isLockedFor = (cell: CellState, selection: Preselection) =>
    (cell.excluded &&
      selection.kind !== "clear" &&
      selection.kind !== "exclude") ||
    conflictsWithCell(cell, selection);

  const warnSkippedConflicts = (count: number, selection: Preselection) => {
    if (count === 0) return;
    const pilot = selection.kind === "pilotTeam";
    const one = pilot
      ? "Une case n'est pas tenue en rang 1 par un autre membre du groupement de ce mandataire : elle n'a pas été modifiée."
      : "Une case contient déjà un contrat incompatible avec la pré-sélection : elle n'a pas été modifiée.";
    const many = pilot
      ? `${count} cases ne sont pas tenues en rang 1 par un autre membre du groupement de ce mandataire : elles n'ont pas été modifiées.`
      : `${count} cases contiennent déjà un contrat incompatible avec la pré-sélection : elles n'ont pas été modifiées.`;
    toaster.error({
      title: "Cases non modifiées",
      description: count === 1 ? one : many,
    });
  };

  const contractTypeOf = (contractId: number) =>
    allContracts.find((c) => c.id === contractId)?.type;

  /**
   * Links `added` contracts to a cell, each replacing the cell's contract of
   * the same rang: an intervention has a single active contract per rang.
   * A team whose contract no longer covers the cell is dropped with it.
   */
  const withContracts = (cell: CellState, added: number[]): CellState => {
    const replacedTypes = new Set(added.map(contractTypeOf));
    const contractIds = [
      ...cell.contractIds.filter(
        (id) => added.includes(id) || !replacedTypes.has(contractTypeOf(id)),
      ),
      ...added.filter((id) => !cell.contractIds.includes(id)),
    ];
    const keepsTeam = (teamId: number | null) => {
      if (isNil(teamId)) return null;
      const teamContractId = allTeams.find((t) => t.id === teamId)?.contractId;
      return isNotNil(teamContractId) && contractIds.includes(teamContractId)
        ? teamId
        : null;
    };
    return {
      ...cell,
      contractIds,
      teamRang1Id: keepsTeam(cell.teamRang1Id),
      teamRang2Id: keepsTeam(cell.teamRang2Id),
    };
  };

  const paintCell = (cell: CellState, selection: Preselection): CellState => {
    if (selection.kind === "clear") return { ...emptyCell };
    if (selection.kind === "exclude") return { ...emptyCell, excluded: true };
    if (isLockedFor(cell, selection)) return cell;
    if (selection.kind === "intervention") {
      // An intervention that already has links is left as is.
      return isCellEmpty(cell) ? { ...emptyCell, keepEmpty: true } : cell;
    }
    if (selection.kind === "pilotTeam") {
      return { ...cell, bossTeamIds: [selection.teamId] };
    }
    const links = cascadeLinks(selection);
    if (isNil(links)) return cell;
    const linked = withContracts(cell, links.contractIds);
    return {
      ...linked,
      teamRang1Id: links.teamRang1Id ?? linked.teamRang1Id,
      teamRang2Id: links.teamRang2Id ?? linked.teamRang2Id,
      keepEmpty: false,
    };
  };

  /**
   * Undoes the pré-sélection's own link on a cell (the cascaded rang 1
   * contract stays). An intervention that exists in base is kept as "?"
   * rather than deleted; a cell ticked only in the draft goes back to blank.
   */
  const unpaintCell = (
    cell: CellState,
    selection: Preselection,
    keepIntervention: boolean,
  ): CellState => {
    let next: CellState = cell;
    const teamContractOf = (teamId: number | null) =>
      isNotNil(teamId)
        ? allTeams.find((t) => t.id === teamId)?.contractId
        : undefined;
    switch (selection.kind) {
      case "intervention":
      case "exclude":
        return { ...emptyCell };
      case "clear":
        return cell;
      case "contractRang1":
        next = {
          ...cell,
          contractIds: cell.contractIds.filter(
            (id) => id !== selection.contractId,
          ),
          teamRang1Id:
            teamContractOf(cell.teamRang1Id) === selection.contractId
              ? null
              : cell.teamRang1Id,
        };
        break;
      case "contractRang2":
        next = {
          ...cell,
          contractIds: cell.contractIds.filter(
            (id) => id !== selection.contractId,
          ),
          teamRang2Id:
            teamContractOf(cell.teamRang2Id) === selection.contractId
              ? null
              : cell.teamRang2Id,
        };
        break;
      case "teamRang1":
        next = { ...cell, teamRang1Id: null };
        break;
      case "teamRang2":
        next = { ...cell, teamRang2Id: null };
        break;
      case "pilotTeam":
        next = {
          ...cell,
          bossTeamIds: cell.bossTeamIds.filter((id) => id !== selection.teamId),
        };
        break;
    }
    return { ...next, keepEmpty: isCellEmpty(next) && keepIntervention };
  };

  const toggleCell = (
    performanceId: number,
    zoneId: number,
    selection: Preselection,
  ) => {
    const current = getCell(performanceId, zoneId);
    const applied = isPreselectionApplied(current, selection);
    if (!applied && isLockedFor(current, selection)) {
      if (!current.excluded) warnSkippedConflicts(1, selection);
      return;
    }
    applyCellOrWarn(
      performanceId,
      zoneId,
      applied
        ? unpaintCell(
            current,
            selection,
            hasServerIntervention(performanceId, zoneId),
          )
        : paintCell(current, selection),
    );
  };

  const paintRange = (
    from: GridPosition,
    to: GridPosition,
    selection: Preselection,
  ) => {
    const rowMin = Math.min(from.row, to.row);
    const rowMax = Math.max(from.row, to.row);
    const colMin = Math.min(from.col, to.col);
    const colMax = Math.max(from.col, to.col);
    let conflicts = 0;
    let locked = 0;
    for (let row = rowMin; row <= rowMax; row++) {
      const performance = flatPerformances[row];
      if (isNil(performance)) continue;
      for (let col = colMin; col <= colMax; col++) {
        const zone = flatZones[col];
        if (isNil(zone)) continue;
        const current = getCell(performance.id, zone.id);
        if (isLockedFor(current, selection)) {
          if (!current.excluded) conflicts++;
          continue;
        }
        if (
          !applyCell(performance.id, zone.id, paintCell(current, selection))
        ) {
          locked++;
        }
      }
    }
    warnSkippedConflicts(conflicts, selection);
    warnLockedContracts(locked);
  };

  // Ticking a row or column header applies the pré-sélection to the whole
  // prestation or zone, like a shift+click range.
  const paintRow = (row: number, selection: Preselection) =>
    paintRange({ row, col: 0 }, { row, col: totalZones - 1 }, selection);

  const paintColumn = (col: number, selection: Preselection) =>
    paintRange(
      { row: 0, col },
      { row: flatPerformances.length - 1, col },
      selection,
    );

  const handleCellMouseDown = (row: number, col: number, shiftKey: boolean) => {
    if (isNil(preselection)) return;
    setIsDragging(true);
    setHasDragged(false);
    if (shiftKey && isNotNil(selectionAnchor)) {
      setSelectionEnd({ row, col });
    } else {
      setSelectionAnchor({ row, col });
      setSelectionEnd({ row, col });
    }
  };

  const handleCellMouseEnter = (row: number, col: number) => {
    if (!isDragging) return;
    setHasDragged(true);
    setSelectionEnd({ row, col });
  };

  const handleCellMouseUp = (
    row: number,
    col: number,
    shiftKey: boolean,
    performanceId: number,
    zoneId: number,
  ) => {
    // Without a pré-sélection, a click opens the detailed cell editor.
    if (isNil(preselection)) {
      setEditing({ performanceId, zoneId });
      return;
    }
    setIsDragging(false);
    setHasDragged(false);
    const isRange = (shiftKey || hasDragged) && isNotNil(selectionAnchor);
    if (isRange) {
      paintRange(selectionAnchor, { row, col }, preselection);
    } else {
      toggleCell(performanceId, zoneId, preselection);
      setSelectionAnchor({ row, col });
    }
    // The anchor stays as the start of the next shift+click range.
    setSelectionEnd(null);
  };

  // Safety net: always stop dragging on mouseup, even released outside a cell.
  useEffect(() => {
    if (!isDragging) return;
    const onWindowMouseUp = () => {
      setIsDragging(false);
      setSelectionEnd(null);
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, [isDragging]);

  const choosePreselection = (next: Preselection | null) => {
    setPreselection(next);
    setPickerKind(null);
    setSelectionAnchor(null);
    setSelectionEnd(null);
  };

  // A rang 2 contract or team whose rang 1 contract is missing or no longer
  // active cannot cascade a rang 1 contract into the cells: flag it.
  const noParentDetail =
    "Sans donneur d'ordre rang 1 actif : à choisir dans le panneau des contrats";

  const pickerItems = (
    kind: PickerKind,
  ): { id: number; label: string; detail: string | null; pilot: boolean }[] => {
    switch (kind) {
      case "contractRang1":
        return rang1Contracts.map((c) => ({
          id: c.id,
          label: c.label,
          detail: null,
          pilot: false,
        }));
      case "contractRang2":
        return rang2Contracts.map((c) => {
          const parentId = parentRang1ContractId(c.id);
          return {
            id: c.id,
            label: c.label,
            detail: isNotNil(parentId)
              ? `Sous-traitant de ${contractLabel(parentId)}`
              : noParentDetail,
            pilot: false,
          };
        });
      case "teamRang1":
        // The mandataire's teams come back as pilots of the interventions
        // held by the other members of its groupement.
        return [
          ...rang1Teams.map((t) => ({
            id: t.id,
            label: t.name,
            detail: contractLabel(t.contractId),
            pilot: false,
          })),
          ...rang1Teams
            .filter((t) =>
              groups.some((g) => g.mandataireContractId === t.contractId),
            )
            .map((t) => ({
              id: t.id,
              label: t.name,
              detail: `Mandataire : ${contractLabel(t.contractId)}`,
              pilot: true,
            })),
        ];
      case "teamRang2":
        return rang2Teams.map((t) => ({
          id: t.id,
          label: t.name,
          detail: isNotNil(parentRang1ContractId(t.contractId))
            ? contractLabel(t.contractId)
            : `${contractLabel(t.contractId)} — ${noParentDetail}`,
          pilot: false,
        }));
    }
  };

  const pickItem = (kind: PickerKind, id: number, pilot: boolean) => {
    switch (kind) {
      case "contractRang1":
      case "contractRang2":
        choosePreselection({ kind, contractId: id });
        break;
      case "teamRang1":
      case "teamRang2":
        choosePreselection({ kind: pilot ? "pilotTeam" : kind, teamId: id });
        break;
    }
  };

  // Label of the value held by the active pré-sélection: once chosen, the
  // highlighted picto shows only that value.
  const preselectionValue = (kind: PickerKind): string | null => {
    if (kind === "teamRang1" && preselection?.kind === "pilotTeam") {
      return `⚑ ${teamName(preselection.teamId)}`;
    }
    if (isNil(preselection) || preselection.kind !== kind) return null;
    switch (preselection.kind) {
      case "contractRang1":
      case "contractRang2":
        return contractLabel(preselection.contractId);
      case "teamRang1":
      case "teamRang2":
        return teamName(preselection.teamId);
    }
    return null;
  };

  const previewKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!isDragging || isNil(selectionAnchor) || isNil(selectionEnd)) {
      return keys;
    }
    const rowMin = Math.min(selectionAnchor.row, selectionEnd.row);
    const rowMax = Math.max(selectionAnchor.row, selectionEnd.row);
    const colMin = Math.min(selectionAnchor.col, selectionEnd.col);
    const colMax = Math.max(selectionAnchor.col, selectionEnd.col);
    for (let row = rowMin; row <= rowMax; row++) {
      const performance = flatPerformances[row];
      if (isNil(performance)) continue;
      for (let col = colMin; col <= colMax; col++) {
        const zone = flatZones[col];
        if (isNil(zone)) continue;
        keys.add(cellKey(performance.id, zone.id));
      }
    }
    return keys;
  }, [isDragging, selectionAnchor, selectionEnd, flatPerformances, flatZones]);

  const editingState = editing
    ? getCell(editing.performanceId, editing.zoneId)
    : emptyCell;

  // Pilot candidates: the mandataire's active teams, plus those already
  // checked (an interrupted team can still be unchecked).
  const editingPilotContractId = pilotContractIdFor(editingState.contractIds);
  const piloteCandidates: MatrixTeam[] = allTeams.filter(
    (t) =>
      t.contractId === editingPilotContractId &&
      (teams.some((active) => active.id === t.id) ||
        editingState.bossTeamIds.includes(t.id)),
  );

  if (loading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center text-gray-500">
        Chargement de la matrice...
      </div>
    );
  }

  if (lots.length === 0 || totalZones === 0) {
    return (
      <p className="text-sm text-zinc-400">
        Définissez d'abord des lots/prestations et des bâtiments/zones pour
        éditer la matrice.
      </p>
    );
  }

  const draftActions = draft.size > 0 && (
    <div className="ml-auto flex shrink-0 items-center gap-2">
      <span className="whitespace-nowrap text-sm text-amber-600 dark:text-amber-400">
        Grille : {draft.size} cellule(s) modifiée(s) non enregistrée(s)
      </span>
      <Button
        outline
        onClick={() => confirmDiscardDraft(() => setDraft(new Map()))}
        disabled={saving}
      >
        Annuler
      </Button>
      <Button onClick={() => setConfirmingSave(true)} disabled={saving}>
        {saving ? "Enregistrement..." : "Enregistrer"}
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* One line, title included, at the minimum resolution with the
          contracts panel closed (PO): compact buttons, help text in the
          label's tooltip, long pré-sélection values truncated. */}
      <div className="mb-3 flex shrink-0 items-center gap-1 rounded-lg border border-zinc-200 p-2 dark:border-zinc-800">
        {headerContent}
        <span
          className="shrink-0 cursor-help whitespace-nowrap text-xs font-medium text-zinc-700 dark:text-zinc-300"
          title="Choisissez une pré-sélection, puis cochez les cases une à une, une plage avec Maj+clic, ou toute une ligne ou colonne via son en-tête."
        >
          Pré-sélection :
        </span>
        <ToolbarButton
          active={preselection?.kind === "intervention"}
          title="Générer les interventions des cases cochées, sans contrat ni équipe"
          icon={QuestionIcon}
          onClick={() =>
            choosePreselection(
              preselection?.kind === "intervention"
                ? null
                : { kind: "intervention" },
            )
          }
        >
          Intervention
        </ToolbarButton>
        <ToolbarButton
          active={preselection?.kind === "contractRang1"}
          title={
            preselectionValue("contractRang1") ??
            "Pré-sélectionner un contrat rang 1"
          }
          icon={FileTextIcon}
          onClick={() => setPickerKind("contractRang1")}
        >
          {preselectionValue("contractRang1") ?? "Contrat rang 1"}
        </ToolbarButton>
        <ToolbarButton
          active={
            preselection?.kind === "teamRang1" ||
            preselection?.kind === "pilotTeam"
          }
          title={
            preselectionValue("teamRang1") ??
            "Pré-sélectionner une équipe rang 1"
          }
          icon={UsersThreeIcon}
          onClick={() => setPickerKind("teamRang1")}
        >
          {preselectionValue("teamRang1") ?? "Équipe rang 1"}
        </ToolbarButton>
        <ToolbarButton
          active={preselection?.kind === "contractRang2"}
          title={
            preselectionValue("contractRang2") ??
            "Pré-sélectionner un contrat rang 2"
          }
          icon={FileTextIcon}
          onClick={() => setPickerKind("contractRang2")}
        >
          {preselectionValue("contractRang2") ?? "Contrat rang 2"}
        </ToolbarButton>
        <ToolbarButton
          active={preselection?.kind === "teamRang2"}
          title={
            preselectionValue("teamRang2") ??
            "Pré-sélectionner une équipe rang 2"
          }
          icon={UsersThreeIcon}
          onClick={() => setPickerKind("teamRang2")}
        >
          {preselectionValue("teamRang2") ?? "Équipe rang 2"}
        </ToolbarButton>
        <ToolbarButton
          active={preselection?.kind === "clear"}
          title="Vider les cases cochées"
          icon={EraserIcon}
          onClick={() =>
            choosePreselection(
              preselection?.kind === "clear" ? null : { kind: "clear" },
            )
          }
        >
          Vider
        </ToolbarButton>
        <ToolbarButton
          active={preselection?.kind === "exclude"}
          title="Marquer les cases cochées comme non concernées : aucune intervention n'y aura lieu"
          icon={ProhibitIcon}
          onClick={() =>
            choosePreselection(
              preselection?.kind === "exclude" ? null : { kind: "exclude" },
            )
          }
        >
          Non concernée
        </ToolbarButton>
        {isNotNil(preselection) && (
          <button
            type="button"
            aria-label="Retirer la pré-sélection"
            title="Retirer la pré-sélection"
            onClick={() => choosePreselection(null)}
            className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            <XIcon className="size-4" />
          </button>
        )}

        {isNil(draftActionsTarget) && draftActions}
      </div>
      {isNotNil(draftActionsTarget) &&
        draftActions &&
        createPortal(draftActions, draftActionsTarget)}

      {/* Sole scroll region of the matrix: fills the remaining height so only
          the grid's own content scrolls (both axes — the zone/column count is
          variable per screen-design.md), with the "Prestation" row header and
          the column headers pinned via sticky, never the surrounding page. */}
      <div
        className="isolate min-h-0 flex-1 overflow-auto overscroll-none rounded-lg border border-zinc-200 dark:border-zinc-800"
        style={stickyOffsets}
        onScroll={(event) => pushLotBands(event.currentTarget)}
      >
        <table className="w-full table-fixed select-none border-separate border-spacing-0 text-xs">
          <colgroup>
            <col className="w-56" />
            {flatZones.map((zone) => (
              <col key={zone.id} className="w-16" />
            ))}
          </colgroup>
          <thead ref={headerRef}>
            <tr>
              <th
                className="sticky left-0 top-0 z-30 h-9 truncate border-b border-r border-zinc-200 bg-zinc-50 p-2 text-left dark:border-zinc-800 dark:bg-zinc-900"
                rowSpan={2}
              >
                Prestation
              </th>
              {buildings
                // A building with no zones contributes no columns to the
                // grid (the zone header row and body rows already skip it
                // via flatMap) — colSpan={0} would still render a phantom
                // 1-wide column (browsers clamp 0 to 1), shifting every
                // zone header after it one column left of its real building.
                .filter((building) => building.zones.length > 0)
                .map((building) => (
                  <th
                    key={building.id}
                    colSpan={building.zones.length}
                    className="sticky top-0 z-20 h-9 border-b border-l border-zinc-200 bg-zinc-50 p-2 text-center font-medium dark:border-zinc-800 dark:bg-zinc-900"
                    title={building.name}
                  >
                    {/* Pinned just right of the "Prestation" corner (w-56) and
                        to the right edge, so a building wider than the screen
                        keeps its name in view while any of its zones is
                        visible. Not on the cell itself: `truncate` (overflow
                        hidden) there would make the cell the label's scroll
                        container. */}
                    <span className="sticky right-2 left-58 inline-block max-w-full truncate align-top">
                      {building.shortName ?? building.name}
                    </span>
                  </th>
                ))}
            </tr>
            <tr>
              {buildings.flatMap((building) =>
                building.zones.map((zone) => (
                  <th
                    key={zone.id}
                    className="sticky top-[var(--sticky-head-1,2.25rem)] z-20 border-b border-l border-zinc-200 bg-zinc-50 p-1 text-center font-normal text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900"
                    title={zone.name}
                  >
                    <HeaderTick
                      label={zone.name}
                      enabled={isNotNil(preselection)}
                      onTick={() => {
                        if (isNil(preselection)) return;
                        paintColumn(
                          flatZones.findIndex((z) => z.id === zone.id),
                          preselection,
                        );
                      }}
                    >
                      <div className="mx-auto flex h-24 items-start justify-center">
                        <span
                          className="whitespace-nowrap"
                          style={{
                            writingMode: "vertical-rl",
                            maxHeight: "6rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {zone.shortName ?? zone.name}
                        </span>
                      </div>
                    </HeaderTick>
                  </th>
                )),
              )}
            </tr>
          </thead>
          {lots.map((lot) => (
            <tbody key={lot.id}>
              <tr>
                {/* The lot name stays under the column headers while its
                    prestations scroll (`top` = measured thead height, less
                    the push of `pushLotBands`), and stays left while the
                    zones scroll. The name and the rest of the row are two
                    cells, each pinned itself: Safari 15 mispositions a sticky
                    element nested in a sticky cell while scrolling. z-15/16:
                    above the prestation headers (z-10) that scroll under them,
                    the name above the rest of its row, both below the column
                    headers (z-20). */}
                <th
                  className="sticky left-0 top-[calc(var(--sticky-head-total,8.75rem)-var(--lot-band-push,0px))] z-16 truncate border-b border-zinc-200 bg-zinc-100 p-1.5 text-left font-semibold text-zinc-700 dark:border-zinc-800 dark:bg-zinc-800 dark:text-zinc-300"
                  title={lot.name}
                >
                  {lot.nLot ? `${lot.nLot} - ` : ""}
                  {lot.name}
                </th>
                {totalZones > 0 && (
                  <td
                    colSpan={totalZones}
                    className="sticky top-[calc(var(--sticky-head-total,8.75rem)-var(--lot-band-push,0px))] z-15 border-b border-zinc-200 bg-zinc-100 p-0 dark:border-zinc-800 dark:bg-zinc-800"
                  />
                )}
              </tr>
              {lot.performances.map((performance) => {
                const row = flatPerformances.findIndex(
                  (p) => p.id === performance.id,
                );
                return (
                  <tr key={performance.id}>
                    <th
                      className="sticky left-0 z-10 truncate border-b border-r border-zinc-200 bg-white p-2 text-left font-normal text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300"
                      title={performance.name}
                    >
                      <HeaderTick
                        label={performance.name}
                        enabled={isNotNil(preselection)}
                        onTick={() => {
                          if (isNil(preselection)) return;
                          paintRow(row, preselection);
                        }}
                      >
                        {performance.name}
                      </HeaderTick>
                    </th>
                    {buildings.flatMap((building) =>
                      building.zones.map((zone) => {
                        const col = flatZones.findIndex(
                          (z) => z.id === zone.id,
                        );
                        const cell = getCell(performance.id, zone.id);
                        const dirty = isDirtyCell(performance.id, zone.id);
                        const selected = previewKeys.has(
                          cellKey(performance.id, zone.id),
                        );
                        const isAnchor =
                          isNotNil(preselection) &&
                          selectionAnchor?.row === row &&
                          selectionAnchor?.col === col;
                        const r1 = teamName(cell.teamRang1Id);
                        const r2 = teamName(cell.teamRang2Id);
                        const r1Contracts = cellContractAbbrevs(
                          cell.contractIds,
                          "travaux_n_1",
                          cell.teamRang1Id,
                        );
                        const r2Contracts = cellContractAbbrevs(
                          cell.contractIds,
                          "travaux_n_2",
                          cell.teamRang2Id,
                        );
                        const r1Abbrev =
                          r1Contracts.join(", ") ||
                          teamContractAbbrev(cell.teamRang1Id);
                        const r2Abbrev =
                          r2Contracts.join(", ") ||
                          teamContractAbbrev(cell.teamRang2Id);
                        const showRang1 = isNotNil(r1) || isNotNil(r1Abbrev);
                        const showRang2 = isNotNil(r2) || isNotNil(r2Abbrev);
                        const pilotId = cell.bossTeamIds[0] ?? null;
                        const pilotLabel = isNotNil(pilotId)
                          ? [teamContractAbbrev(pilotId), teamName(pilotId)]
                              .filter(isNotNil)
                              .join(" ")
                          : null;
                        const hasContract = cell.contractIds.length > 0;
                        const isPending = isCellEmpty(cell) && cell.keepEmpty;
                        // The API flags a cell per rang: a rang 2
                        // interrupted under a rang 1 still in place counts.
                        const needsCellReprise =
                          !dirty &&
                          needsReprise.has(cellKey(performance.id, zone.id));
                        const cellTitle = [
                          showRang1
                            ? `Rang 1 : ${r1Abbrev ?? ""} ${r1 ?? ""}`.trim()
                            : null,
                          isNotNil(pilotLabel)
                            ? `Pilotée par : ${pilotLabel}`
                            : null,
                          showRang2
                            ? `Rang 2 : ${r2Abbrev ?? ""} ${r2 ?? ""}`.trim()
                            : null,
                        ]
                          .filter(isNotNil)
                          .join(" — ");
                        return (
                          <td
                            key={zone.id}
                            className={cn(
                              "border-b border-l border-zinc-200 p-0 text-center dark:border-zinc-800",
                              dirty && "bg-amber-100 dark:bg-amber-900/30",
                              needsCellReprise &&
                                "bg-red-50 dark:bg-red-900/20",
                              cell.excluded &&
                                !dirty &&
                                "bg-zinc-100 dark:bg-zinc-800/60",
                              selected &&
                                "ring-2 ring-inset ring-blue-500 dark:ring-blue-400",
                              !selected &&
                                isAnchor &&
                                "ring-1 ring-inset ring-blue-400 dark:ring-blue-500",
                            )}
                          >
                            <button
                              type="button"
                              title={cellTitle || undefined}
                              aria-label={`${performance.name} — ${zone.name}`}
                              onMouseDown={(e) =>
                                handleCellMouseDown(row, col, e.shiftKey)
                              }
                              onMouseEnter={() =>
                                handleCellMouseEnter(row, col)
                              }
                              onMouseUp={(e) =>
                                handleCellMouseUp(
                                  row,
                                  col,
                                  e.shiftKey,
                                  performance.id,
                                  zone.id,
                                )
                              }
                              className="relative flex h-full min-h-[3.25rem] w-full items-center justify-center px-1 py-1 leading-none hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            >
                              {showRang1 && (
                                <span className="absolute left-0.5 top-0.5 flex max-w-[calc(100%-0.5rem)] flex-col items-start text-[9px] text-blue-700 dark:text-blue-300">
                                  <span
                                    className={cn(
                                      "w-full truncate font-semibold",
                                      needsCellReprise && "pr-3",
                                    )}
                                  >
                                    {r1Abbrev ?? "?"}
                                  </span>
                                  {isNotNil(r1) && (
                                    <span className="w-full truncate">
                                      {r1}
                                    </span>
                                  )}
                                  {isNotNil(pilotLabel) && (
                                    <span className="w-full truncate text-amber-700 dark:text-amber-400">
                                      ⚑ {pilotLabel}
                                    </span>
                                  )}
                                </span>
                              )}
                              {showRang2 && (
                                <span className="absolute bottom-0.5 right-0.5 flex max-w-[calc(100%-0.5rem)] flex-col items-end text-[9px] text-zinc-500">
                                  <span className="w-full truncate text-right font-semibold">
                                    {r2Abbrev ?? "?"}
                                  </span>
                                  {isNotNil(r2) && (
                                    <span className="w-full truncate text-right">
                                      {r2}
                                    </span>
                                  )}
                                </span>
                              )}
                              {!showRang1 && !showRang2 && hasContract && (
                                <span className="text-zinc-400">●</span>
                              )}
                              {cell.excluded && (
                                <span
                                  className="text-zinc-400"
                                  title="Non concernée : aucune intervention n'aura lieu dans cette case"
                                >
                                  <ProhibitIcon className="size-3.5" />
                                </span>
                              )}
                              {isPending && (
                                <span
                                  className="text-zinc-400"
                                  title="Intervention à faire : périmètre pas encore défini"
                                >
                                  ?
                                </span>
                              )}
                              {needsCellReprise && (
                                <span
                                  className="absolute right-0.5 top-0.5 text-[10px] text-red-600 dark:text-red-400"
                                  title="Contrat interrompu : intervention à reprendre par un nouveau contrat"
                                >
                                  ⚠
                                </span>
                              )}
                            </button>
                          </td>
                        );
                      }),
                    )}
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>

      {/* Pré-sélection picker: choosing a value closes it and highlights the
          matching picto. */}
      <Modal.Root
        open={isNotNil(pickerKind)}
        onOpenChange={(details) => {
          if (!details.open) setPickerKind(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  {isNotNil(pickerKind) ? pickerTitles[pickerKind] : ""}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {isNotNil(pickerKind) &&
                  (pickerItems(pickerKind).length === 0 ? (
                    <p className="text-sm text-zinc-400">
                      {pickerEmptyMessages[pickerKind]}
                    </p>
                  ) : (
                    <ul className="max-h-80 divide-y divide-zinc-100 overflow-y-auto dark:divide-zinc-800">
                      {pickerItems(pickerKind).map((item, index, items) => (
                        <li key={`${item.pilot}-${item.id}`}>
                          {item.pilot && !items[index - 1]?.pilot && (
                            <div className="px-2 pb-1 pt-3 text-xs text-zinc-500">
                              <p className="font-medium">
                                Pilote des interventions des autres membres du
                                groupement
                              </p>
                              <p>{singlePilotHint}</p>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              pickItem(pickerKind, item.id, item.pilot)
                            }
                            className="flex w-full flex-col items-start px-2 py-1.5 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            <span className="text-zinc-900 dark:text-zinc-100">
                              {item.label}
                            </span>
                            {isNotNil(item.detail) && (
                              <span className="text-xs text-zinc-500">
                                {item.detail}
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ))}
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setPickerKind(null)}>
                  Annuler
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      <ConfirmationDialog
        open={confirmingSave}
        title={
          reassignedCount > 0
            ? "Changer le contrat d'interventions"
            : "Enregistrer les modifications"
        }
        confirmLabel="Enregistrer"
        cancelLabel="Revenir à la matrice"
        onConfirm={save}
        onCancel={() => setConfirmingSave(false)}
      >
        {reassignedCount === 0
          ? "Les modifications apportées à la grille vont être enregistrées."
          : reassignedCount === 1
            ? "Une intervention change de contrat rang 1 ou rang 2, ou perd le sien. Confirmez-vous ce changement d'affectation ?"
            : `${reassignedCount} interventions changent de contrat rang 1 ou rang 2, ou perdent le leur. Confirmez-vous ce changement d'affectation ?`}
      </ConfirmationDialog>

      {/* Cell editor */}
      <Modal.Root
        open={isNotNil(editing)}
        onOpenChange={(details) => {
          if (!details.open) setEditing(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Périmètre de la cellule</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="space-y-4">
                  <div>
                    <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      Contrats couvrant cette cellule
                    </span>
                    {contracts.length === 0 ? (
                      <p className="text-xs text-zinc-400">
                        Aucun contrat travaux
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {contracts.map((contract) => (
                          <div
                            key={contract.id}
                            className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300"
                          >
                            <Checkbox
                              checked={editingState.contractIds.includes(
                                contract.id,
                              )}
                              onCheckedChange={(d) => {
                                if (isNil(editing)) return;
                                const checked = d.checked === true;
                                const next = checked
                                  ? withContracts(editingState, [contract.id])
                                  : withContracts(
                                      {
                                        ...editingState,
                                        contractIds:
                                          editingState.contractIds.filter(
                                            (id) => id !== contract.id,
                                          ),
                                      },
                                      [],
                                    );
                                applyCellOrWarn(
                                  editing.performanceId,
                                  editing.zoneId,
                                  { ...next, keepEmpty: false },
                                );
                              }}
                            />
                            <span>{contract.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <CellTeamSelect
                    label="Équipe rang 1"
                    teams={rang1Teams}
                    value={editingState.teamRang1Id}
                    onChange={(teamRang1Id) => {
                      if (isNil(editing)) return;
                      const team = teams.find((t) => t.id === teamRang1Id);
                      const linked = isNotNil(team)
                        ? withContracts(editingState, teamContractIds(team))
                        : editingState;
                      applyCellOrWarn(editing.performanceId, editing.zoneId, {
                        ...linked,
                        teamRang1Id,
                        keepEmpty: false,
                      });
                    }}
                  />

                  <CellTeamSelect
                    label="Équipe sous-traitante (rang 2)"
                    teams={rang2Teams}
                    value={editingState.teamRang2Id}
                    onChange={(teamRang2Id) => {
                      if (isNil(editing)) return;
                      const team = teams.find((t) => t.id === teamRang2Id);
                      const linked = isNotNil(team)
                        ? withContracts(editingState, teamContractIds(team))
                        : editingState;
                      applyCellOrWarn(editing.performanceId, editing.zoneId, {
                        ...linked,
                        teamRang2Id,
                        keepEmpty: false,
                      });
                    }}
                  />

                  {piloteCandidates.length > 0 && (
                    <div>
                      <CellTeamSelect
                        label="Équipe pilote (mandataire)"
                        teams={piloteCandidates}
                        value={editingState.bossTeamIds[0] ?? null}
                        onChange={(pilotId) => {
                          if (isNil(editing)) return;
                          applyCellOrWarn(
                            editing.performanceId,
                            editing.zoneId,
                            {
                              ...editingState,
                              bossTeamIds: isNotNil(pilotId) ? [pilotId] : [],
                              keepEmpty: false,
                            },
                          );
                        }}
                      />
                      <p className="mt-1 text-xs text-zinc-500">
                        {singlePilotHint}
                      </p>
                    </div>
                  )}

                  {(!isCellEmpty(editingState) ||
                    editingState.keepEmpty ||
                    editingState.excluded) && (
                    <Button
                      plain
                      onClick={() => {
                        if (isNil(editing)) return;
                        applyCellOrWarn(editing.performanceId, editing.zoneId, {
                          ...emptyCell,
                          keepEmpty: false,
                        });
                      }}
                    >
                      Vider la cellule
                    </Button>
                  )}

                  {isCellEmpty(editingState) && !editingState.keepEmpty && (
                    <Button
                      plain
                      onClick={() => {
                        if (isNil(editing)) return;
                        applyCellOrWarn(editing.performanceId, editing.zoneId, {
                          ...emptyCell,
                          keepEmpty: true,
                        });
                      }}
                    >
                      Marquer comme « à faire »
                    </Button>
                  )}

                  {!editingState.excluded && (
                    <Button
                      plain
                      onClick={() => {
                        if (isNil(editing)) return;
                        applyCellOrWarn(editing.performanceId, editing.zoneId, {
                          ...emptyCell,
                          excluded: true,
                        });
                      }}
                    >
                      Marquer comme « non concernée »
                    </Button>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => setEditing(null)}>Fermer</Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}

type CellTeamSelectProps = {
  label: string;
  teams: MatrixTeam[];
  value: number | null;
  onChange: (teamId: number | null) => void;
};

type HeaderTickProps = {
  /** Prestation or zone name, used in the accessible label. */
  label: string;
  enabled: boolean;
  onTick: () => void;
  children: ReactNode;
};

/**
 * Row/column header content: with a pré-sélection active it becomes a button
 * applying it to the whole prestation or zone; otherwise plain text.
 */
function HeaderTick({ label, enabled, onTick, children }: HeaderTickProps) {
  if (!enabled) return <>{children}</>;
  return (
    <button
      type="button"
      aria-label={`Appliquer la pré-sélection à ${label}`}
      title={`Appliquer la pré-sélection à ${label}`}
      onClick={onTick}
      className="block w-full truncate [text-align:inherit] hover:text-blue-600 dark:hover:text-blue-400"
    >
      {children}
    </button>
  );
}

function CellTeamSelect({
  label,
  teams,
  value,
  onChange,
}: CellTeamSelectProps) {
  const options: SelectOption[] = [
    { label: "Aucune", value: "0" },
    ...teams.map((t) => ({ label: t.name, value: String(t.id) })),
  ];
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
        {label}
      </span>
      <Select
        options={options}
        value={[String(value ?? 0)]}
        onValueChange={(details) => {
          const val = details.value[0];
          onChange(val && val !== "0" ? Number(val) : null);
        }}
        placeholder="Sélectionner une équipe"
        disabled={teams.length === 0}
      />
    </div>
  );
}

export type ToolbarButtonProps = {
  /** Toggle buttons only (pré-sélection): pressed state. */
  active?: boolean;
  title: string;
  icon: Icon;
  onClick: () => void;
  children: ReactNode;
};

// Smaller than the ui `Button` (which has no compact size) so the whole
// toolbar holds on one line at 1280px.
export function ToolbarButton({
  active,
  title,
  icon: ButtonIcon,
  onClick,
  children,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex max-w-32 shrink-0 cursor-pointer items-center gap-1 rounded-md border px-1.5 py-1 text-xs font-medium",
        active
          ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-500"
          : "border-zinc-950/10 bg-white text-zinc-950 hover:bg-zinc-950/2.5 dark:border-white/15 dark:bg-zinc-900 dark:text-white dark:hover:bg-white/5",
      )}
    >
      <ButtonIcon
        className={cn(
          "size-3.5 shrink-0",
          active ? "text-white" : "text-zinc-500 dark:text-zinc-400",
        )}
      />
      <span className="truncate">{children}</span>
    </button>
  );
}
